import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Copy, FileText, Loader2, ShieldCheck, CreditCard, Lock, Landmark } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { useT, useLanguage } from "@/i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const RESUME_KEY = "winfield_last_reservation";

// ---------- Localised copy ----------
const T = {
  back: { en: "Back to site", sr: "Nazad na sajt" },
  loading: { en: "Loading your reservation…", sr: "Učitavanje vaše rezervacije…" },
  notFound: { en: "Reservation not found", sr: "Rezervacija nije pronađena" },
  notFoundBody: {
    en: "We couldn't find a reservation with that link. The URL may be incorrect or the reservation may have been removed. If you believe this is a mistake, contact us at hello@winfieldtoken.com.",
    sr: "Nismo mogli da pronađemo rezervaciju sa ovim linkom. URL je možda netačan ili je rezervacija uklonjena. Ako mislite da je ovo greška, kontaktirajte nas na hello@winfieldtoken.com.",
  },
  headlineSigned: { en: "Pre-agreement signed", sr: "Predugovor potpisan" },
  headlinePaid: { en: "Reservation complete", sr: "Rezervacija završena" },
  contract: { en: "Your contract number", sr: "Vaš broj ugovora" },
  status: { en: "Status", sr: "Status" },
  bodySigned: {
    en: "Your pre-agreement is signed and saved. To finalise your reservation, complete the payment securely by card below. Your reservation is held for 7 days from signing.",
    sr: "Vaš predugovor je potpisan i sačuvan. Da biste finalizovali rezervaciju, završite plaćanje sigurno karticom ispod. Vaša rezervacija se drži 7 dana od potpisa.",
  },
  bodyPaid: {
    en: "Thank you. We've recorded your bank-transfer confirmation. Our team will reach out within 1–2 business days to confirm receipt and walk you through the next steps.",
    sr: "Hvala vam. Zabeleženo je da ste poslali bankarski transfer. Naš tim će vas kontaktirati u roku od 1–2 radna dana radi potvrde uplate i sledećih koraka.",
  },
  bookmark: {
    en: "Bookmark this page",
    sr: "Sačuvajte ovu stranicu",
  },
  bookmarkSub: {
    en: "Save this link so you can return any time to view your contract, download the signed PDF, or check the payment instructions.",
    sr: "Sačuvajte ovaj link kako biste se mogli vratiti u bilo koje vreme da pregledate ugovor, preuzmete potpisani PDF ili proverite uputstva za plaćanje.",
  },
  copyLink: { en: "Copy link", sr: "Kopiraj link" },
  copied: { en: "Copied!", sr: "Kopirano!" },
  depositReminder: { en: "Send your deposit", sr: "Pošaljite depozit" },
  documentHeading: { en: "Your signed pre-agreement", sr: "Vaš potpisani predugovor" },
  documentSub: {
    en: "Save a copy for your records. We've also kept it on file under your contract number.",
    sr: "Sačuvajte kopiju za svoju evidenciju. Mi takođe čuvamo dokument pod vašim brojem ugovora.",
  },
  download: { en: "Download signed PDF", sr: "Preuzmite potpisani PDF" },
  contactHeading: { en: "Need help?", sr: "Potrebna vam je pomoć?" },
  contactBody: {
    en: "Reply to your confirmation email or write to us at hello@winfieldtoken.com — quote your contract number and we'll respond within one business day.",
    sr: "Odgovorite na potvrdni email ili nam pišite na hello@winfieldtoken.com — navedite svoj broj ugovora i odgovorićemo u roku od jednog radnog dana.",
  },
  payCard: {
    heading: { en: "Complete your payment", sr: "Završite plaćanje" },
    amountLabel: { en: "Amount due", sr: "Iznos za uplatu" },
    choose: { en: "Choose how to pay", sr: "Izaberite način plaćanja" },
    full: { en: "Pay in full", sr: "Plati u celosti" },
    fullNote: { en: "One card payment", sr: "Jedno plaćanje karticom" },
    deposit: { en: "Pay deposit", sr: "Plati depozit" },
    depositNote: {
      en: "Reserve now, then 24 × €500 monthly",
      sr: "Rezervišite sada, zatim 24 × 500 € mesečno",
    },
    secure: {
      en: "Secure payment by card processed by Monri WebPay. Cards accepted: Visa, Mastercard, Maestro, Dina. Winfield never sees or stores your card number.",
      sr: "Sigurno plaćanje karticom obrađuje Monri WebPay. Prihvaćene kartice: Visa, Mastercard, Maestro, Dina. Winfield nikada ne vidi niti čuva broj vaše kartice.",
    },
    payNow: { en: "Pay by card", sr: "Plati karticom" },
    redirecting: { en: "Redirecting…", sr: "Preusmeravanje…" },
    paidToast: { en: "Payment received — thank you!", sr: "Uplata primljena — hvala vam!" },
    cancelledToast: { en: "Payment cancelled. You can try again any time.", sr: "Plaćanje otkazano. Možete pokušati ponovo." },
    depositDone: {
      en: "Deposit received. Your monthly instalments ({count} × €{monthly}) are listed below — pay each one by card when it's due.",
      sr: "Depozit je primljen. Vaše mesečne rate ({count} × {monthly} €) navedene su ispod — platite svaku karticom kada dospe.",
    },
  },
  plan: {
    heading: { en: "Monthly instalment plan", sr: "Plan mesečnih rata" },
    progress: { en: "paid", sr: "plaćeno" },
    remaining: { en: "remaining", sr: "preostalo" },
    colNo: { en: "#", sr: "#" },
    colDue: { en: "Due date", sr: "Dospeće" },
    colAmount: { en: "Amount", sr: "Iznos" },
    colStatus: { en: "Status", sr: "Status" },
    statusPaid: { en: "Paid", sr: "Plaćeno" },
    statusDue: { en: "Due now", sr: "Dospelo" },
    statusUpcoming: { en: "Upcoming", sr: "Predstoji" },
    pay: { en: "Pay €500", sr: "Plati 500 €" },
    scanTitle: { en: "Pay from your phone", sr: "Platite sa telefona" },
    scanNote: {
      en: "Scan this code to open your reservation on your phone and pay the next instalment.",
      sr: "Skenirajte ovaj kôd da otvorite rezervaciju na telefonu i platite sledeću ratu.",
    },
    allPaid: { en: "All instalments paid — thank you!", sr: "Sve rate su plaćene — hvala vam!" },
  },
  invoices: {
    heading: { en: "Invoices", sr: "Računi" },
    note: { en: "Your invoices. Click to view or download the PDF.", sr: "Vaši računi. Kliknite da pregledate ili preuzmete PDF." },
    advance: { en: "Advance", sr: "Avans" },
    full: { en: "Full payment", sr: "Plaćanje u celosti" },
    instalment: { en: "Instalment", sr: "Rata" },
    proforma: { en: "Proforma", sr: "Predračun" },
    statusDue: { en: "Awaiting payment", sr: "Čeka uplatu" },
    statusPaid: { en: "Paid", sr: "Plaćeno" },
    download: { en: "View PDF", sr: "Pregledaj PDF" },
  },
  sepaAwait: {
    toast: { en: "Proforma created — see the bank details below.", sr: "Predračun je kreiran — pogledajte podatke za uplatu ispod." },
    heading: { en: "Awaiting your bank transfer", sr: "Čekamo vaš bankarski transfer" },
    note: {
      en: "Please transfer the amount below by SEPA. Use the reference exactly as shown — it ties the payment to your contract. Your proforma is in the Invoices section. We'll confirm and issue your advance invoice once the funds arrive.",
      sr: "Molimo prenesite iznos ispod putem SEPA. Koristite poziv na broj tačno kako je prikazano — povezuje uplatu sa vašim ugovorom. Vaš predračun je u odeljku Računi. Potvrdićemo i izdati avansni račun čim sredstva stignu.",
    },
    beneficiary: { en: "Beneficiary", sr: "Primalac" },
    iban: { en: "IBAN", sr: "IBAN" },
    swift: { en: "SWIFT / BIC", sr: "SWIFT / BIC" },
    reference: { en: "Reference", sr: "Poziv na broj" },
    amount: { en: "Amount", sr: "Iznos" },
  },
};

// ---------- Reservation page ----------
export default function Reservation() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = useT();
  const { lang } = useLanguage();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [copied, setCopied] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payOption, setPayOption] = useState("full");
  const [invoices, setInvoices] = useState([]);

  const loadInvoices = () => {
    fetch(`${API}/api/pre-agreements/${id}/invoices`)
      .then((r) => (r.ok ? r.json() : { invoices: [] }))
      .then((d) => setInvoices(d.invoices || []))
      .catch(() => {});
  };

  // One-time toast for the ?paid / ?cancelled flags Monri redirects back with.
  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      toast.success(T.payCard.paidToast[lang]);
      searchParams.delete("paid");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("cancelled") === "1") {
      toast.error(T.payCard.cancelledToast[lang]);
      searchParams.delete("cancelled");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("sepa") === "1") {
      toast.success(T.sepaAwait.toast[lang]);
      searchParams.delete("sepa");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitMonri = async (body) => {
    if (paying) return;
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/payments/monri/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Failed to start payment");
      const form = document.createElement("form");
      form.method = "POST";
      form.action = data.action;
      Object.entries(data.fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      toast.error(err.message || "Network error");
      setPaying(false);
    }
  };

  const startCardPayment = () => submitMonri({ pre_agreement_id: id, payment_option: payOption });
  const payInstalment = (n) => submitMonri({ pre_agreement_id: id, payment_option: "instalment", installment_number: n });

  const startSepaProforma = async () => {
    if (paying) return;
    setPaying(true);
    try {
      const res = await fetch(`${API}/api/payments/sepa/proforma`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pre_agreement_id: id, payment_option: payOption }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed to start bank transfer");
      window.location.href = `/r/${id}?sepa=1`;
    } catch (err) {
      toast.error(err.message || "Network error");
      setPaying(false);
    }
  };

  // Deep link from a monthly reminder email: /r/:id?pay_instalment=N auto-starts that payment.
  useEffect(() => {
    const n = parseInt(searchParams.get("pay_instalment") || "", 10);
    if (n && state.data?.status === "deposit_paid" && !paying) {
      const entry = (state.data.installment_plan || []).find((p) => p.n === n);
      if (entry && entry.status !== "paid") {
        searchParams.delete("pay_instalment");
        setSearchParams(searchParams, { replace: true });
        payInstalment(n);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/pre-agreements/${id}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json())?.detail || "Not found");
        return r.json();
      })
      .then((doc) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data: doc });
        if (["deposit_paid", "paid", "awaiting_sepa"].includes(doc.status)) loadInvoices();
        // Remember this reservation for the homepage resume banner.
        try {
          window.localStorage.setItem(
            RESUME_KEY,
            JSON.stringify({ id, contract_number: doc.contract_number, status: doc.status }),
          );
        } catch { /* ignore */ }
      })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, error: err.message, data: null });
      });
    return () => { cancelled = true; };
  }, [id]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setCopied(true);
        toast.success(T.copied[lang]);
        setTimeout(() => setCopied(false), 2000);
      },
      () => toast.error("Copy failed"),
    );
  };

  return (
    <main className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F]">
      <header className="border-b border-[#E8E7E2] bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="reservation-back-home" className="inline-flex items-center gap-2 text-sm font-medium text-[#62646B] hover:text-[#0A0A0F]">
            <ArrowLeft size={14} /> {t(T.back)}
          </Link>
          <div className="flex items-center gap-3">
            <img src="/img/winn-coin.png" alt="Winfield" className="w-8 h-8 object-contain" />
            <span className="font-heading font-semibold tracking-tight">$WINN</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        {state.loading && (
          <div className="flex items-center gap-3 text-sm text-[#62646B]">
            <Loader2 size={16} className="animate-spin" /> {t(T.loading)}
          </div>
        )}

        {state.error && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-[#E8E7E2] rounded-3xl p-10"
            data-testid="reservation-not-found"
          >
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t(T.notFound)}</h1>
            <p className="text-sm text-[#62646B] leading-relaxed mt-3 max-w-2xl">{t(T.notFoundBody)}</p>
            <Link
              to="/lookup"
              data-testid="reservation-lookup-link"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors"
            >
              {lang === "sr" ? "Pronađi rezervaciju" : "Look up by email"}
            </Link>
          </motion.div>
        )}

        {state.data && (() => {
          const doc = state.data;
          const paid = doc.status === "paid";
          const depositPaid = doc.status === "deposit_paid";
          const awaitingSepa = doc.status === "awaiting_sepa";
          const canPay = doc.status === "signed";
          const dueProforma = invoices.find((i) => i.kind === "proforma_sepa" && i.status === "due");
          return (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="reservation-view"
              className="space-y-6"
            >
              {/* Headline */}
              <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10 wf-glow">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#0A0A0F] text-white">
                    {paid ? <Check size={22} /> : <ShieldCheck size={22} />}
                  </span>
                  <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">
                    {paid ? t(T.headlinePaid) : t(T.headlineSigned)}
                  </h1>
                </div>

                <div className="mt-7 grid sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-2xl border border-[#E8E7E2]">
                    <p className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">{t(T.contract)}</p>
                    <p data-testid="reservation-contract-number" className="font-mono text-xl mt-2">{doc.contract_number}</p>
                  </div>
                  <div className="p-5 rounded-2xl border border-[#E8E7E2]">
                    <p className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">{t(T.status)}</p>
                    <p data-testid="reservation-status" className="text-base font-semibold mt-2 capitalize">{doc.status.replace(/_/g, " ")}</p>
                  </div>
                </div>

                <p className="text-sm text-[#62646B] leading-relaxed mt-6 max-w-2xl">
                  {paid ? t(T.bodyPaid) : depositPaid ? "" : t(T.bodySigned)}
                </p>
              </div>

              {/* Bookmark callout */}
              <div className="bg-[#FBF8F1] border border-[#E8E7E2] rounded-3xl p-6 lg:p-8" data-testid="reservation-bookmark">
                <h2 className="font-heading text-base sm:text-lg font-semibold flex items-center gap-2">
                  🔖 <span>{t(T.bookmark)}</span>
                </h2>
                <p className="text-xs sm:text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{t(T.bookmarkSub)}</p>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <code className="text-xs font-mono text-[#0A0A0F] bg-white border border-[#E8E7E2] rounded-lg px-3 py-2 break-all flex-1 min-w-0">{shareUrl}</code>
                  <button
                    type="button"
                    onClick={copyLink}
                    data-testid="reservation-copy-link"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-xs sm:text-sm font-semibold transition-colors shrink-0"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t(T.copied) : t(T.copyLink)}
                  </button>
                </div>
              </div>

              {/* SEPA awaiting bank transfer */}
              {awaitingSepa && (
                <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10" data-testid="reservation-sepa-await">
                  <div className="flex items-center gap-3">
                    <Landmark size={20} className="text-[#0A0A0F]" />
                    <h2 className="font-heading text-xl font-semibold tracking-tight">{t(T.sepaAwait.heading)}</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{t(T.sepaAwait.note)}</p>
                  <div className="mt-5 divide-y divide-[#E8E7E2] border border-[#E8E7E2] rounded-2xl overflow-hidden text-sm">
                    {[
                      { k: t(T.sepaAwait.beneficiary), v: "Winfield Inženjering doo Užice" },
                      { k: t(T.sepaAwait.iban), v: "RS35 2050 0701 0056 9173 49", mono: true },
                      { k: t(T.sepaAwait.swift), v: "KOBBRSBG", mono: true },
                      { k: t(T.sepaAwait.reference), v: doc.contract_number, mono: true },
                      ...(dueProforma ? [{ k: t(T.sepaAwait.amount), v: `€${Number(dueProforma.amount_eur).toLocaleString("de-DE")}`, mono: true }] : []),
                    ].map((row) => (
                      <div key={row.k} className="flex items-center justify-between gap-3 px-4 py-3">
                        <span className="text-[10px] uppercase tracking-widest text-[#62646B] font-semibold">{row.k}</span>
                        <span className={`${row.mono ? "font-mono" : ""} text-sm text-[#0A0A0F] text-right`}>{row.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pay by card — only if not yet paid */}
              {canPay && doc.package_snapshot && (
                <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10" data-testid="reservation-pay-block">
                  <div className="flex items-center gap-3">
                    {doc.payment_method === "sepa" ? <Landmark size={20} className="text-[#0A0A0F]" /> : <CreditCard size={20} className="text-[#0A0A0F]" />}
                    <h2 className="font-heading text-xl font-semibold tracking-tight">{doc.payment_method === "sepa" ? t(T.sepaAwait.heading) : t(T.payCard.heading)}</h2>
                  </div>
                  <p className="text-xs uppercase tracking-widest text-[#62646B] font-semibold mt-5">{t(T.payCard.choose)}</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-4">
                    {[
                      { key: "full", title: t(T.payCard.full), amount: Number(doc.package_snapshot.package_price_eur ?? 14900), note: t(T.payCard.fullNote), testid: "r-payopt-full" },
                      { key: "deposit", title: t(T.payCard.deposit), amount: Number(doc.package_snapshot.deposit_eur ?? 2900), note: `${lang === "sr" ? "Rezervišite sada, zatim" : "Reserve now, then"} ${doc.package_snapshot.installment_count ?? 24} × €${Number(doc.package_snapshot.monthly_instalment_eur ?? 500).toLocaleString(lang === "sr" ? "de-DE" : "en-US")} ${lang === "sr" ? "mesečno" : "monthly"}`, testid: "r-payopt-deposit" },
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.key}
                        onClick={() => setPayOption(opt.key)}
                        data-testid={opt.testid}
                        aria-pressed={payOption === opt.key}
                        className={`text-left p-5 rounded-2xl border-2 transition-colors ${payOption === opt.key ? "border-[#0A0A0F] bg-[#FBF8F1]" : "border-[#E8E7E2] hover:border-[#9B9DA4]"}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">{opt.title}</span>
                          <span className={`w-4 h-4 rounded-full border-2 ${payOption === opt.key ? "border-[#0A0A0F] bg-[#0A0A0F]" : "border-[#C9CDD6]"}`} />
                        </div>
                        <p className="font-heading text-2xl font-semibold mt-2">€{opt.amount.toLocaleString("de-DE")}</p>
                        <p className="text-xs text-[#62646B] mt-1">{opt.note}</p>
                      </button>
                    ))}
                  </div>
                  <div className="mt-5 flex items-start gap-2.5 text-xs text-[#62646B]">
                    <Lock size={15} className="text-[#0A0A0F] shrink-0 mt-0.5" />
                    <span>{doc.payment_method === "sepa" ? t(T.sepaAwait.note) : t(T.payCard.secure)}</span>
                  </div>
                  {doc.payment_method === "sepa" ? (
                    <button
                      type="button"
                      onClick={startSepaProforma}
                      disabled={paying}
                      data-testid="reservation-pay-sepa"
                      className="inline-flex items-center gap-2 mt-6 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
                    >
                      {paying
                        ? (<><Loader2 size={16} className="animate-spin" />{t(T.payCard.redirecting)}</>)
                        : (<><Landmark size={16} />{lang === "sr" ? "Nastavi sa bankovnim transferom" : "Continue by bank transfer"}</>)}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={startCardPayment}
                      disabled={paying}
                      data-testid="reservation-pay-card"
                      className="inline-flex items-center gap-2 mt-6 px-8 py-3.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white font-semibold transition-colors disabled:opacity-60"
                    >
                      {paying
                        ? (<><Loader2 size={16} className="animate-spin" />{t(T.payCard.redirecting)}</>)
                        : (<><CreditCard size={16} />{t(T.payCard.payNow)}</>)}
                    </button>
                  )}
                </div>
              )}

              {/* Instalment plan — shown once deposit is paid */}
              {depositPaid && Array.isArray(doc.installment_plan) && doc.installment_plan.length > 0 && (() => {
                const plan = doc.installment_plan;
                const paidCount = plan.filter((p) => p.status === "paid").length;
                const paidEur = paidCount * (plan[0]?.amount_eur || 500);
                const totalEur = plan.reduce((s, p) => s + (p.amount_eur || 0), 0);
                const todayStr = new Date().toISOString().slice(0, 10);
                const nextDue = plan.find((p) => p.status !== "paid");
                return (
                  <div className="bg-white border border-[#E8E7E2] rounded-3xl p-6 lg:p-9" data-testid="reservation-instalments">
                    <div className="flex items-center gap-3">
                      <CreditCard size={20} className="text-[#0A0A0F]" />
                      <h2 className="font-heading text-xl font-semibold tracking-tight">{t(T.plan.heading)}</h2>
                    </div>
                    <p className="text-xs sm:text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{t(T.payCard.depositDone).replace("{count}", doc.package_snapshot?.installment_count ?? 24).replace("{monthly}", Number(doc.package_snapshot?.monthly_instalment_eur ?? 500).toLocaleString(lang === "sr" ? "de-DE" : "en-US"))}</p>

                    {/* progress */}
                    <div className="mt-5">
                      <div className="flex items-center justify-between text-xs font-semibold">
                        <span data-testid="instalments-progress">{paidCount}/{plan.length} · €{paidEur.toLocaleString("de-DE")} {t(T.plan.progress)}</span>
                        <span className="text-[#62646B]">€{(totalEur - paidEur).toLocaleString("de-DE")} {t(T.plan.remaining)}</span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-[#EEEDE8] overflow-hidden">
                        <div className="h-full bg-[#0A0A0F] transition-all" style={{ width: `${(paidCount / plan.length) * 100}%` }} />
                      </div>
                    </div>

                    {/* QR to pay from phone */}
                    {nextDue && (
                      <div className="mt-6 flex flex-col sm:flex-row items-start gap-4 p-4 rounded-2xl border border-[#E8E7E2] bg-[#FBF8F1]">
                        <div className="bg-white p-2 rounded-xl border border-[#E8E7E2]">
                          <QRCodeCanvas value={`${shareUrl.split("?")[0]}?pay_instalment=${nextDue.n}`} size={92} fgColor="#0A0A0F" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold">{t(T.plan.scanTitle)}</p>
                          <p className="text-xs text-[#62646B] mt-1 max-w-sm">{t(T.plan.scanNote)}</p>
                        </div>
                      </div>
                    )}
                    {!nextDue && (
                      <p className="mt-5 text-sm font-semibold text-[#0A0A0F] inline-flex items-center gap-2"><Check size={16} /> {t(T.plan.allPaid)}</p>
                    )}

                    {/* schedule */}
                    <div className="mt-6 overflow-hidden rounded-2xl border border-[#E8E7E2]">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-[#0A0A0F] text-white text-left">
                            <th className="py-2.5 px-3 font-medium text-xs">{t(T.plan.colNo)}</th>
                            <th className="py-2.5 px-3 font-medium text-xs">{t(T.plan.colDue)}</th>
                            <th className="py-2.5 px-3 font-medium text-xs">{t(T.plan.colAmount)}</th>
                            <th className="py-2.5 px-3 font-medium text-xs text-right">{t(T.plan.colStatus)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.map((p) => {
                            const isPaid = p.status === "paid";
                            const isDue = !isPaid && p.due_date <= todayStr;
                            const isNext = !isPaid && nextDue && p.n === nextDue.n;
                            return (
                              <tr key={p.n} className="border-t border-[#E8E7E2]" data-testid={`instalment-row-${p.n}`}>
                                <td className="py-2.5 px-3 font-mono text-[#62646B]">{p.n}</td>
                                <td className="py-2.5 px-3">{p.due_date}</td>
                                <td className="py-2.5 px-3 font-medium">€{(p.amount_eur || 0).toLocaleString("de-DE")}</td>
                                <td className="py-2.5 px-3 text-right">
                                  {isPaid ? (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700"><Check size={13} /> {t(T.plan.statusPaid)}</span>
                                  ) : isNext ? (
                                    <button
                                      type="button"
                                      onClick={() => payInstalment(p.n)}
                                      disabled={paying}
                                      data-testid={`pay-instalment-${p.n}`}
                                      className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-xs font-semibold transition-colors disabled:opacity-60"
                                    >
                                      {paying ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />} {lang === "sr" ? "Plati" : "Pay"} €{Number(p.amount_eur ?? doc.package_snapshot?.monthly_instalment_eur ?? 500).toLocaleString(lang === "sr" ? "de-DE" : "en-US")}
                                    </button>
                                  ) : (
                                    <span className={`text-xs font-medium ${isDue ? "text-amber-700" : "text-[#9B9DA4]"}`}>{isDue ? t(T.plan.statusDue) : t(T.plan.statusUpcoming)}</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Invoices */}
              {invoices.length > 0 && (
                <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10" data-testid="reservation-invoices">
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-[#0A0A0F]" />
                    <h2 className="font-heading text-xl font-semibold tracking-tight">{t(T.invoices.heading)}</h2>
                  </div>
                  <p className="text-xs sm:text-sm text-[#62646B] mt-2">{t(T.invoices.note)}</p>
                  <div className="mt-5 divide-y divide-[#E8E7E2] border border-[#E8E7E2] rounded-2xl overflow-hidden">
                    {invoices.map((inv) => {
                      const kindLabel = inv.kind === "proforma_sepa" ? t(T.invoices.proforma) : null;
                      const label = inv.payment_kind === "instalment"
                        ? `${t(T.invoices.instalment)} ${inv.installment_number}`
                        : inv.payment_kind === "full" ? t(T.invoices.full) : t(T.invoices.advance);
                      const isDue = inv.status === "due";
                      return (
                        <div key={inv.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`invoice-row-${inv.invoice_no}`}>
                          <div className="min-w-0">
                            <p className="font-mono text-sm flex items-center gap-2">
                              {inv.invoice_no}
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-sans font-semibold ${isDue ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                                {isDue ? t(T.invoices.statusDue) : t(T.invoices.statusPaid)}
                              </span>
                            </p>
                            <p className="text-xs text-[#62646B] mt-0.5">{kindLabel ? `${kindLabel} · ` : ""}{label} · €{Number(inv.amount_eur).toLocaleString("de-DE")}</p>
                          </div>
                          <a
                            href={`${API}/api/invoices/${inv.id}/pdf`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`invoice-download-${inv.invoice_no}`}
                            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-[#E8E7E2] hover:border-[#0A0A0F] text-xs font-medium transition-colors"
                          >
                            <FileText size={13} /> {t(T.invoices.download)}
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Signed PDF */}
              <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10">
                <div className="flex items-center gap-3">
                  <FileText size={20} className="text-[#0A0A0F]" />
                  <h2 className="font-heading text-xl font-semibold tracking-tight">{t(T.documentHeading)}</h2>
                </div>
                <p className="text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{t(T.documentSub)}</p>
                <a
                  href={`${API}/api/pre-agreements/${doc.id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reservation-download-pdf"
                  className="inline-flex items-center gap-2 mt-5 px-6 py-3 rounded-full bg-[#0A0A0F] hover:bg-[#1F1F26] text-white text-sm font-semibold transition-colors"
                >
                  <FileText size={15} /> {t(T.download)}
                </a>
              </div>

              {/* Contact */}
              <div className="bg-white border border-[#E8E7E2] rounded-3xl p-8 lg:p-10">
                <h3 className="font-heading text-lg font-semibold">{t(T.contactHeading)}</h3>
                <p className="text-sm text-[#62646B] leading-relaxed mt-2 max-w-2xl">{t(T.contactBody)}</p>
              </div>
            </motion.div>
          );
        })()}
      </div>
    </main>
  );
}
